import React from 'react';
import { CircleQuestionMark } from "lucide-react";
import { ICON_SIZE } from "components/common/constants";
import { Tooltip } from "components/tooltip/Tooltip";
import * as reduxSelectors from '../store/application.reducers';
import {connect} from 'react-redux';
import translate from '../translations/translations.wrapper.jsx';
import ConfigurationWrapper from '../configuration/ConfigurationWrapper.component';
import {QuantitySelector} from 'components/common/QuantitySelector.component';
import * as Api from 'utils/api/api';
import * as _ from "lodash";
import {DOCUMENT_TYPE} from 'utils/constants';
import "./dueSettings.scss";
import {userHasAccessRights} from "utils/accessRights.function";
import {handleError, insufficientRightsError} from "utils/errorHandle.function";
import {canViewConfiguration} from "../menu/Menu.component";
import { RadioButton } from "components/common/RadioButton.component";
import { Button, ButtonType } from "components/common/Button.component";
import { useModalContext } from "components/modals/ModalContext";
import { HEADING_INFO_TOOLTIP_PLACE, SHARED_TOOLTIP_ID } from "components/tooltip/AppTooltip.component";

const TASK_DUE_PERIOD = "taskDuePeriod";
const MONTH_DAY = "documentDueDayOfMonth";
const PERIOD_DUE = "documentDuePeriod";
const USE_DAY_OF_MONTH = "useDayOfMonth";

export class DueSettings extends ConfigurationWrapper {

    constructor(props) {
        super(props);
        this.state = {
            initialSettings: [],
            settings: [],
            okEnabled: false
        };
        this.cancel = this.cancel.bind(this);
        this.getData = this.getData.bind(this);
        this.updateSettings = this.updateSettings.bind(this);
        this.save = this.save.bind(this);

    }

    componentDidMount(props) {
        if (!userHasAccessRights(this.props.userData.userRoles, this.props.location) || !canViewConfiguration(this.props.userData.userRoles)) {
            handleError(insufficientRightsError);
        }

        super.componentDidMount(props);
    }

    cancel() {
        this.getData();
        this.props.blocker.unblockNavigation();
    }

    getData() {
        Api.getDocumentTypesConfiguration(this.props.currentContextId).then(settings => {
            this.setState({
                settings: settings.result,
                initialSettings: settings.result,
                okEnabled: false,
            });
        });
    }

    updateSettings(value, docType, prop) {
        let enableOk = false;
        let oldState = _.cloneDeep(this.state.settings);
        let docIndex = _.findIndex(oldState, value => {
            return value.documentType === docType
        });
        if (docIndex !== -1) {
            let validValue = value;
            oldState[docIndex][prop] = validValue;
            enableOk = this.state.initialSettings[docIndex][prop] !== value;
        }

        this.setState({
            okEnabled: enableOk,
            settings: oldState
        });
        this.props.blocker.shouldWaitForConfirmation(enableOk);
    }

    save() {
        Api.postDocumentTypesConfiguration(this.props.currentContextId, this.state.settings).then(response => {
            this.setState({
                okEnabled: false
            });
        });
        this.props.blocker.unblockNavigation();
    }


    buildContent() {

        let documentsConfigurations = this.state.settings.map((config, index) => {
            return (<div key={index}>
                <div className="ga-text-md font-weight-semibold cropped-text pl-32 doc-types"
                     data-for={SHARED_TOOLTIP_ID} data-tip={`due-settings-doctype-tooltip-${config.documentType}`}>
                    {this.props.translate("documentTypes." + DOCUMENT_TYPE.asString(config.documentType))}
                </div>
                <Tooltip
                    id={`due-settings-doctype-tooltip-${config.documentType}`}
                    content={() => this.props.translate("documentTypes." + DOCUMENT_TYPE.asString(config.documentType))}
                />
                <OverdueSettings key={index} config={config} updateSettings={this.updateSettings}
                                 toggleUseDayOfMonth={this.toggleUseDayOfMonth}
                                 lockedEdit={!this.props.userRoles.systemAdministrator}/>
            </div>)
        });

        return (
            <main className="page w-100 overflow-y-auto d-flex justify-content-center align-items-start">
                <div className="py-16 mb-48 w-75 settings-page__container">
                    <h2 className="ps-4 mb-4 d-flex align-items-center">
                        {this.props.translate("dueSettings.header")}
                        <span className="ms-4"
                              aria-label={this.props.translate("dueSettings.headerInfo")}
                              data-for={SHARED_TOOLTIP_ID} data-tip="due-settings-header-info-tooltip"
                              data-place={HEADING_INFO_TOOLTIP_PLACE}>
                            <CircleQuestionMark size={ICON_SIZE.MEDIUM}/>
                        </span>
                        <Tooltip
                            id="due-settings-header-info-tooltip"
                            content={() => this.props.translate("dueSettings.headerInfo")}
                        />
                    </h2>
                    <div className="bg-white radius-4 p-4 setting_collapsable-main">
                        <div className="d-flex" id="due_settings">
                            <OverdueInfo translate={this.props.translate}/>
                            <div className="values-panel d-flex">
                                {documentsConfigurations}
                            </div>
                        </div>
                        <div className="d-flex justify-content-end gap-8 mt-4">
                            <Button type={ButtonType.PRIMARY}
                                    onClick={this.save}
                                    disabled={!this.state.okEnabled}
                                    dataTestId={"__tst_save"}
                                    text={this.props.translate("settings.save")}/>
                            <Button type={ButtonType.SECONDARY}
                                    onClick={this.cancel}
                                    disabled={!this.state.okEnabled}
                                    dataTestId={"__tst_cancel"}
                                    text={this.props.translate("settings.cancel")}/>
                        </div>
                    </div>
                </div>
            </main>
        )
    }
}

const withTranslations = translate(DueSettings);
const mapStateToProps = function (store) {
    return {
        currentContextId: reduxSelectors.getUsersCurrentContextId(store),
        userRoles: reduxSelectors.getUsersRoles(store),
        userData: reduxSelectors.getUsersData(store)
    };
};

const Connected = connect(mapStateToProps, {})(withTranslations);

const DueSettingsWithModal = (props) => {
    const modalContext = useModalContext();
    const {toggleModal} = modalContext || {};
    return <Connected {...props} toggleModal={toggleModal} />;
};

export default DueSettingsWithModal;

const OverdueInfo = (props) => {
    let {translate} = props;
    return (
        <div className="left-panel pe-0">
            <div className="ga-text-lg font-weight-semibold">{translate("dueSettings.taskDueDates")}</div>
            <div>{translate("dueSettings.daysToComplete")}</div>

            <hr className="my-5"/>
            <div className="ga-text-lg font-weight-semibold">{translate("dueSettings.documentDueDates")}</div>
            <div>{translate("dueSettings.documentOverdue")}</div>
            <hr/>
            <div>{translate("dueSettings.documentDeadline")}</div>
        </div>
    )
};

const OverdueSettings = (props) => {
    let {config, lockedEdit} = props;

    return (
        <div className="d-flex flex-column align-items-center w-100">
            <div className="overdue-settings__row px-3">
                <QuantitySelector min={1} max={99} value={config.taskDuePeriod ?? 1}
                              disabled={lockedEdit}
                              strict={true}
                              dataTestId={TASK_DUE_PERIOD}
                              onChange={(e) => props.updateSettings(parseInt(e.target.value, 10), config.documentType, TASK_DUE_PERIOD)}/>
            </div>

            <hr className="my-5"/>
            <div className="overdue-settings__row px-3"/>
            <div className="overdue-settings__row px-3">
                <RadioButton
                    radioId={`monthly-deadline-date-${config.documentType}`}
                    isChecked={config.useDayOfMonth}
                    isDisabled={lockedEdit}
                    onChange={(e) => props.updateSettings(e.target.checked, config.documentType, USE_DAY_OF_MONTH)}
                    label={<QuantitySelector min={1} max={31} value={config.documentDueDayOfMonth ?? 1}
                                          strict={true}
                                          dataTestId={MONTH_DAY}
                                          disabled={lockedEdit || !config.useDayOfMonth}
                                          onChange={(e) => props.updateSettings(parseInt(e.target.value, 10), config.documentType, MONTH_DAY)}/>}
                />
            </div>
            <hr />
            <div className="overdue-settings__row px-3">
                <RadioButton
                    radioId={`days-after-receipt-${config.documentType}`}
                    isChecked={!config.useDayOfMonth}
                    isDisabled={lockedEdit}
                    onChange={(e) => props.updateSettings(!e.target.checked, config.documentType, USE_DAY_OF_MONTH)}
                    label={<QuantitySelector min={1} max={99} value={config.documentDuePeriod ?? 1}
                                              strict={true}
                                              dataTestId={PERIOD_DUE}
                                              disabled={lockedEdit || config.useDayOfMonth}
                                              onChange={(e) => props.updateSettings(parseInt(e.target.value, 10), config.documentType, PERIOD_DUE)}/>}
                />
            </div>
        </div>
    )
};